"use client";

import React, { useState, useEffect, useRef } from "react";
import { LayoutTemplate, MonitorSmartphone, Briefcase, WandSparkles, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  id: "design",
  title: "Landscape Design",
  description: "Bespoke architectural plans that harmonize with your property's unique natural features and terrain.",
  icon: LayoutTemplate,
  hasToggle: false
}, {
  id: "construction",
  title: "Hardscape Installation",
  description: "Expertly crafted stone walls, patios, and walkways built with precision to endure for generations.",
  icon: MonitorSmartphone,
  hasToggle: false
}, {
  id: "maintenance",
  title: "Estate Maintenance",
  description: "Comprehensive lawn care and seasonal management to keep your estate pristine year-round.",
  icon: Briefcase,
  hasToggle: false
}, {
  id: "transformation",
  title: "Property Transformation",
  description: "Watch your outdoor space evolve from neglected grounds to a stunning, functional botanical retreat.",
  icon: WandSparkles,
  hasToggle: true
}];
function StandardCard({
  service,
  index
}: {
  service: typeof servicesData[0];
  index: number;
}) {
  const Icon = service.icon;
  return <Card data-index={index} className="rounded-lg border-primary/20 shadow-sm h-full flex flex-col bg-primary/40 hover:shadow-xl transition-shadow duration-300">
      <CardHeader className="pb-4">
        <div className="size-14 rounded-sm bg-accent/10 text-accent flex items-center justify-center mb-4">
          <Icon className="size-7" strokeWidth={1} />
        </div>
        <CardTitle className="text-xl tracking-tight font-default text-primary-foreground">{service.title}</CardTitle>
        <CardDescription className="line-clamp-2 text-base mt-2 font-default text-primary-foreground/70">
          {service.description}
        </CardDescription>
      </CardHeader>
    </Card>;
}
function RedesignCard({
  service,
  index
}: {
  service: typeof servicesData[0];
  index: number;
}) {
  const [isAfter, setIsAfter] = useState(false);
  const Icon = service.icon;
  return <Card data-index={index} className="rounded-lg border-primary/20 shadow-sm h-full flex flex-col bg-primary/40 hover:shadow-xl transition-shadow duration-300 overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="size-14 rounded-sm bg-accent/10 text-accent flex items-center justify-center mb-4">
            <Icon className="size-7" strokeWidth={1} />
          </div>
          
        </div>
        <CardTitle className="text-xl tracking-tight font-default text-primary-foreground">{service.title}</CardTitle>
        <CardDescription className="line-clamp-2 text-base mt-2 font-default text-primary-foreground/70">
          {service.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="mt-auto pt-4 pb-6">
        <div className="relative h-28 w-full rounded-sm overflow-hidden bg-primary/60 p-3">
          {}
          <div className={cn("absolute inset-0 p-3 flex flex-col gap-2 transition-all duration-500 ease-in-out", isAfter ? "opacity-0 translate-y-4 pointer-events-none" : "opacity-100 translate-y-0")}>
            <div className="h-4 w-1/2 bg-primary/80 rounded-sm mb-1" />
            <div className="h-2 w-full bg-primary/70 rounded-sm" />
            <div className="h-2 w-4/5 bg-primary/70 rounded-sm" />
            <div className="h-8 w-full bg-primary/70 rounded-sm mt-auto flex items-center justify-center">
               
            </div>
          </div>

          {}
          <div className={cn("absolute inset-0 p-3 flex flex-col gap-2 transition-all duration-500 ease-in-out bg-accent/10", isAfter ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none")}>
            <div className="flex items-center justify-between mb-1">
              <div className="h-3 w-1/3 bg-accent/40 rounded-full" />
              <div className="flex gap-1">
                <div className="size-2 bg-accent/40 rounded-full" />
                <div className="size-2 bg-accent/40 rounded-full" />
              </div>
            </div>
            <div className="flex gap-2 h-full">
              <div className="w-1/3 h-full bg-accent/20 rounded-sm flex items-center justify-center">
                 <div className="size-4 rounded-full bg-accent/30" />
              </div>
              <div className="w-2/3 h-full flex flex-col gap-2">
                 <div className="h-2 w-full bg-accent/20 rounded-sm" />
                 <div className="h-2 w-3/4 bg-accent/20 rounded-sm" />
                 <div className="h-full w-full bg-accent/10 rounded-sm mt-auto" />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
}
export default function OurDigitalCraft() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="112239" id="core-landscaping-solutions" className="bg-primary py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div ref={headerRef} className={cn("max-w-2xl transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-primary-foreground mb-4 font-default">
            Core Landscaping Solutions
          </h2>
          <p className="text-lg text-primary-foreground/80 font-default">
            Serving Rockland and Orange County with architectural precision and organic beauty, transforming outdoor spaces into meticulous environments.
          </p>
        </div>

        {}
        <div ref={gridRef} className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 transition-all duration-1000 delay-200 ease-out", isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {servicesData.map((service, i) => service.hasToggle ? <RedesignCard key={service.id} index={i} service={service} /> : <StandardCard key={service.id} index={i} service={service} />)}
        </div>

      </div>
    </section>;
}